/**
 * Recent searches, per device (T66). A2 kept them in a module array, which
 * reset on every reload; here they persist in `localStorage` through Zustand.
 *
 * A convenience, not data: storage that throws (a private window, a full
 * quota) leaves the list working in memory for the session, and nothing is
 * ever sent to the server.
 */
import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import { RECENTS_MAX, remember } from "./model";

const STORAGE_KEY = "mypocketgym-search-recents";

interface RecentsState {
  readonly clear: () => void;
  readonly recents: readonly string[];
  readonly remember: (query: string) => void;
}

/** `localStorage`, or nothing, never a throw. */
const safeStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      // Nothing stored that could be removed.
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      // Kept in memory for this session only.
    }
  },
};

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}

export const useRecentsStore = create<RecentsState>()(
  persist(
    (set) => ({
      clear: () => set({ recents: [] }),
      recents: [],
      remember: (query) =>
        set((state) => ({ recents: remember(state.recents, query) })),
    }),
    {
      merge: (persisted, current) => {
        const recents = (persisted as { recents?: unknown } | undefined)
          ?.recents;
        return isStringList(recents)
          ? { ...current, recents: recents.slice(0, RECENTS_MAX) }
          : current;
      },
      name: STORAGE_KEY,
      partialize: (state) => ({ recents: state.recents }),
      storage: createJSONStorage(() => safeStorage),
    }
  )
);
